import { useMemo, useState } from "react";
import * as Animate from "react-reveal";
import { podcastData } from "../podcastData";

const TOPICS = [
  "All topics",
  ...Array.from(new Set(podcastData.map((episode) => episode.category))),
];

const MiniWaveform = () => (
  <span className="podcast-waveform" aria-hidden="true">
    {[6, 11, 8, 14, 9, 12, 7].map((h, i) => (
      <span
        key={i}
        className="podcast-waveform-bar podcast-waveform-bar--mini rounded-full bg-primary inline-block shrink-0"
        style={{ height: `${h}px`, opacity: 0.4 + (h / 14) * 0.6 }}
      />
    ))}
  </span>
);

const EpisodeCard = ({ episode }) => {
  return (
    <article
      className="podcast-episode-card bg-white border border-[#e7ecf7] h-full rounded-[24px] overflow-hidden flex"
      style={{ boxShadow: "0 17px 45px rgba(31,34,48,0.055)" }}
    >
      <div className="podcast-episode-card-media relative shrink-0">
        <img
          src={episode.image}
          alt={`${episode.name}, ${episode.role}`}
          className="w-full h-full object-cover"
        />
        <div
          className="absolute inset-x-0 bottom-0 pointer-events-none"
          style={{
            top: "55%",
            background: "linear-gradient(transparent, rgba(3,4,95,0.88))",
          }}
          aria-hidden="true"
        />
        <span className="absolute bottom-3 left-3 text-white text-[13px] font-extrabold tracking-[0.04em]">
          {episode.epNumber}
        </span>
      </div>

      <div className="flex-1 flex flex-col justify-between h-full p-6 sm:p-7">
        <div className="flex items-center justify-between gap-3 mb-4">
          <span className="text-[#9a6500] tracking-[0.05em] text-[13px] font-extrabold uppercase">
            {episode.category}
          </span>
          <MiniWaveform />
        </div>
        <h3 className="text-primary tracking-[-0.025em] font-extrabold text-[21px] sm:text-3xl leading-[1.3] mb-4">
          {episode.title}
        </h3>
        <p className="text-[#555b6e] text-lg leading-[1.68] mb-5">
          {episode.description}
        </p>
        <div className="podcast-episode-card-footer flex items-center justify-between gap-3 mt-auto pt-4 border-t border-[#eef1fa]">
          <span>
            <span className="block text-[#101223] font-bold text-[15px]">
              {episode.name}
            </span>
            <span className="block text-[#7a8096] text-[13px]">
              {episode.role}
            </span>
          </span>
          <a
            href={episode.linkUrl}
            target="_blank"
            rel="noreferrer"
            className="podcast-listen-btn inline-flex items-center gap-2 bg-[#eef1fd] text-primary text-[14px] font-extrabold rounded-full shrink-0 hover:bg-[#dfe5fb] transition-colors"
          >
            <span className="podcast-play-dot podcast-play-dot--sm inline-flex items-center justify-center rounded-full bg-primary shrink-0">
              <span
                aria-hidden="true"
                className="podcast-play-triangle podcast-play-triangle--white"
              />
            </span>
            Listen
          </a>
        </div>
      </div>
    </article>
  );
};

const PodcastEpisodeLibrarySection = () => {
  const [search, setSearch] = useState("");
  const [activeTopic, setActiveTopic] = useState("All topics");

  const filtered = useMemo(() => {
    const query = search.trim().toLowerCase();
    return podcastData.filter((episode) => {
      const matchesTopic =
        activeTopic === "All topics" || episode.category === activeTopic;
      const matchesSearch =
        !query ||
        episode.title.toLowerCase().includes(query) ||
        episode.name.toLowerCase().includes(query) ||
        episode.category.toLowerCase().includes(query);
      return matchesTopic && matchesSearch;
    });
  }, [search, activeTopic]);

  return (
    <section
      id="podcast"
      aria-labelledby="podcast-library-title"
      style={{ background: "#f1f4fc" }}
    >
      <div
        className="mx-auto px-[18px] sm:px-[26px] py-[78px] lg:py-[105px]"
        style={{ maxWidth: "1280px" }}
      >
        <Animate.Fade bottom>
          <p className="flex items-center gap-3 text-[#9a6500] tracking-[0.1em] text-[14px] font-extrabold leading-[1.45] mb-5">
            <span className="w-[34px] h-[3px] bg-secondary inline-block shrink-0" />
            THE EPISODE LIBRARY
          </p>
          <div className="flex flex-wrap items-end justify-between gap-4 mb-8">
            <h2
              id="podcast-library-title"
              className="text-primary tracking-[-0.045em] leading-[1.12] m-0 max-w-[700px]"
              style={{ fontSize: "clamp(32px, 4vw, 52px)" }}
            >
              Find a conversation for where you are.
            </h2>
            <span className="podcast-count-pill inline-flex items-center bg-[#e4e7fb] text-primary font-bold text-[14px] rounded-full shrink-0">
              {filtered.length} episode{filtered.length === 1 ? "" : "s"}
            </span>
          </div>

          <div className="podcast-search-bar flex flex-col sm:flex-row items-stretch bg-white border border-[#dfe4f5] rounded-2xl mb-6">
            <label
              className="flex-1 flex items-center gap-3 px-5"
              htmlFor="podcast-search"
            >
              <span aria-hidden="true" className="text-[#8b93ad] text-[17px]">
                &#128269;
              </span>
              <input
                id="podcast-search"
                type="search"
                value={search}
                onChange={(event) => setSearch(event.target.value)}
                placeholder="Search speakers, topics or ideas&hellip;"
                className="podcast-search-input flex-1 border-0 outline-none text-[15px] text-[#333a4d] placeholder-[#8b93ad] bg-transparent py-4"
              />
            </label>
            <div
              className="podcast-search-divider hidden sm:block"
              aria-hidden="true"
            />
            <label
              className="flex items-center gap-2 px-5 py-4 text-[15px]"
              htmlFor="podcast-sort"
            >
              <span className="text-[#8b93ad]">Sort by</span>
              <select
                id="podcast-sort"
                className="podcast-sort-select font-bold text-primary bg-transparent border-0 outline-none"
                defaultValue="Featured"
              >
                <option value="Featured">Featured</option>
              </select>
            </label>
          </div>

          <div className="flex flex-wrap gap-3 mb-10">
            {TOPICS.map((topic) => {
              const isActive = topic === activeTopic;
              return (
                <button
                  key={topic}
                  type="button"
                  onClick={() => setActiveTopic(topic)}
                  className={`podcast-topic-pill inline-flex items-center rounded-full text-[14px] font-bold transition-colors ${
                    isActive
                      ? "bg-primary text-white"
                      : "bg-white text-[#333a4d] border border-[#dfe4f5] hover:border-primary"
                  }`}
                >
                  {topic}
                </button>
              );
            })}
          </div>
        </Animate.Fade>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {filtered.map((episode) => (
            <Animate.Fade bottom key={episode.id}>
              <EpisodeCard episode={episode} />
            </Animate.Fade>
          ))}
        </div>

        {filtered.length === 0 && (
          <p className="text-[#555b6e] text-[16px] text-center py-16">
            No episodes match your search just yet.
          </p>
        )}
      </div>
    </section>
  );
};

export default PodcastEpisodeLibrarySection;
